import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Mic, X, Loader2, Clock, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useVoiceSearch } from '@/hooks/useVoiceSearch';
import { VoiceSearchOverlay } from './VoiceSearchOverlay';
import { cn } from '@/lib/utils';

interface QuickResult {
  id: string;
  title: string;
  type: 'listing' | 'article' | 'video' | 'trainer';
  slug?: string;
}

interface ProfessionalSearchBarProps {
  variant?: 'hero' | 'navbar' | 'page';
  onSearch?: (query: string) => void;
  placeholder?: string;
  showVoice?: boolean;
  showSuggestions?: boolean;
  initialQuery?: string;
  className?: string;
}

const RECENT_SEARCHES_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

export const ProfessionalSearchBar = ({
  variant = 'navbar',
  onSearch,
  placeholder = 'Buscar gimnasios, entrenadores, nutricionistas...',
  showVoice = true,
  showSuggestions = true,
  initialQuery = '',
  className,
}: ProfessionalSearchBarProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<QuickResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showVoiceOverlay, setShowVoiceOverlay] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showRecent, setShowRecent] = useState(false);
  
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const voiceSearch = useVoiceSearch();

  // Load recent searches
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback((searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    
    setRecentSearches(prev => {
      const updated = [trimmed, ...prev.filter(s => s !== trimmed)].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
        setShowRecent(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const searchTerm = `%${query}%`;

        const [listingsRes, articlesRes, videosRes, trainersRes] = await Promise.all([
          supabase
            .from('listings')
            .select('id, name, slug')
            .eq('status', 'active')
            .ilike('name', searchTerm)
            .limit(3),
          supabase
            .from('articles')
            .select('id, title, slug')
            .eq('status', 'published')
            .ilike('title', searchTerm)
            .limit(3),
          supabase
            .from('videos')
            .select('id, titulo, slug')
            .eq('activo', true)
            .ilike('titulo', searchTerm)
            .limit(3),
          supabase
            .from('trainer_profiles_public')
            .select('id, full_name, slug')
            .ilike('full_name', searchTerm)
            .limit(3),
        ]);

        const allResults: QuickResult[] = [
          ...(listingsRes.data || []).map(l => ({
            id: l.id,
            title: l.name,
            type: 'listing' as const,
            slug: l.slug,
          })),
          ...(articlesRes.data || []).map(a => ({
            id: a.id,
            title: a.title,
            type: 'article' as const,
            slug: a.slug,
          })),
          ...(videosRes.data || []).map(v => ({
            id: v.id,
            title: v.titulo,
            type: 'video' as const,
            slug: v.slug,
          })),
          ...(trainersRes.data || []).map(t => ({
            id: t.id,
            title: t.full_name,
            type: 'trainer' as const,
            slug: t.slug,
          })),
        ];

        setResults(allResults);
        setShowResults(true);
        setShowRecent(false);
      } catch (error) {
        console.error('Quick search error:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = query.trim();
    if (trimmed) {
      saveRecentSearch(trimmed);
      setShowResults(false);
      setShowRecent(false);
      
      if (onSearch) {
        onSearch(trimmed);
      } else {
        navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      }
    }
  };

  const handleSelectResult = (result: QuickResult) => {
    setShowResults(false);
    setQuery('');
    
    switch (result.type) {
      case 'listing':
        navigate(`/listing/${result.slug || result.id}`);
        break;
      case 'article':
        navigate(`/articulo/${result.slug}`);
        break;
      case 'video':
        navigate(`/video/${result.slug}`);
        break;
      case 'trainer':
        navigate(`/trainer/${result.slug || result.id}`);
        break;
    }
  };

  const handleRecentSearch = (search: string) => {
    setQuery(search);
    setShowRecent(false);
    if (onSearch) {
      onSearch(search);
    } else {
      navigate(`/search?q=${encodeURIComponent(search)}`);
    }
  };

  const handleVoiceSearch = (transcript: string) => {
    setShowVoiceOverlay(false);
    setQuery(transcript);
    saveRecentSearch(transcript);
    
    if (onSearch) {
      onSearch(transcript);
    } else {
      navigate(`/search?q=${encodeURIComponent(transcript)}`);
    }
  };

  const startVoice = () => {
    if (voiceSearch.isSupported) {
      setShowVoiceOverlay(true);
      voiceSearch.startListening();
    }
  };

  const stopVoice = () => {
    voiceSearch.stopListening();
    setShowVoiceOverlay(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'listing': return 'Gimnasio';
      case 'article': return 'Artículo';
      case 'video': return 'Video';
      case 'trainer': return 'Entrenador';
      default: return '';
    }
  };

  const inputClasses = cn(
    "pl-10 pr-20 transition-all",
    variant === 'hero' && "h-14 text-lg rounded-xl border-2 focus-visible:ring-4",
    variant === 'page' && "h-12 text-base",
    variant === 'navbar' && "h-10"
  );

  return (
    <>
      <div ref={searchRef} className={cn("relative", className)}>
        <form onSubmit={handleSearch} className="relative">
          <div className="relative flex items-center">
            <Search className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground",
              variant === 'hero' ? "w-5 h-5" : "w-4 h-4"
            )} />
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (results.length > 0) setShowResults(true);
                else if (recentSearches.length > 0 && !query) setShowRecent(true);
              }}
              className={inputClasses}
              aria-label="Buscar"
            />
            <div className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1",
              variant === 'hero' && "right-3"
            )}>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              {query && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="h-7 w-7 p-0"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              )}
              {showVoice && voiceSearch.isSupported && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={startVoice}
                  className={cn(
                    "h-7 w-7 p-0",
                    variant === 'hero' && "h-9 w-9"
                  )}
                  aria-label="Buscar por voz"
                >
                  <Mic className={cn(
                    "w-4 h-4",
                    variant === 'hero' && "w-5 h-5"
                  )} aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        </form>

        {/* Quick results dropdown */}
        {showResults && results.length > 0 && showSuggestions && (
          <div className="absolute top-full mt-2 w-full bg-popover rounded-lg border shadow-lg z-50 overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelectResult(result)}
                  className="w-full px-4 py-3 text-left hover:bg-accent flex items-center justify-between transition-colors"
                >
                  <span className="truncate">{result.title}</span>
                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                    {getTypeLabel(result.type)}
                  </span>
                </button>
              ))}
            </div>
            <div className="p-2 border-t bg-muted/50">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSearch()}
                className="w-full text-xs"
              >
                Ver todos los resultados para "{query}"
              </Button>
            </div>
          </div>
        )}

        {/* Recent searches dropdown */}
        {showRecent && recentSearches.length > 0 && !query && showSuggestions && (
          <div className="absolute top-full mt-2 w-full bg-popover rounded-lg border shadow-lg z-50 overflow-hidden">
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 text-sm text-muted-foreground px-2">
                <Clock className="w-4 h-4" />
                <span>Búsquedas recientes</span>
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearch(search)}
                  className="w-full px-4 py-3 text-left hover:bg-accent flex items-center gap-2 transition-colors"
                >
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <span className="truncate">{search}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Voice search overlay */}
      <VoiceSearchOverlay
        isOpen={showVoiceOverlay}
        voiceState={voiceSearch.voiceState}
        transcript={voiceSearch.transcript}
        interimTranscript={voiceSearch.interimTranscript}
        error={voiceSearch.error}
        onClose={stopVoice}
        onSearch={handleVoiceSearch}
      />
    </>
  );
};
